'use client';

import type {
  ErrorContext,
  SuggestionSelectionProps
} from '@carvana/merch-search-bar';
import MerchSearchBar from '@carvana/merch-search-bar';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';
import { OPEN_TEXT_SEARCH_EXPERIMENT } from '~/constants/common/openTextSearch';
import { apikUrl, assetsCdnUrl } from '~/constants/environment';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useIsMobile, useIsTablet } from '~/context/common/DeviceContext';
import { useConfiguration } from '~/context/common/StaticContext';
import { useUserLocation } from '~/context/common/UserContext';
import { useDealershipId } from '~/context/dealership/StaticContext';
import useIsInPriceDropSuppressionExperiment from '~/hooks/common/experiments/useIsInPriceDropSuppressionExperiment';
import useIsInTextSearchExperiment from '~/hooks/common/experiments/useIsInTextSearchExperiment';
import useTrackRendered from '~/hooks/common/telemetry/useTrackRendered';
import { generateErrorLogger } from '~/utilities/loggers';
import NotFoundSearchBarOverlay from './NotFoundSearchBarOverlay';

const NotFoundSearchBarModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "NotFoundSearchBarModal" */ './NotFoundSearchBarModal'
    ),
  { ssr: false }
);

export default function NotFoundSearchBar() {
  const router = useRouter();
  const [inputValue, setInputValue] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const trackEvent = useTrackEvent();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isTextSearchEnabled = useIsInTextSearchExperiment();
  const suppressPriceDropContent = useIsInPriceDropSuppressionExperiment();
  const { state: geolocationState = '' } = useUserLocation();
  const dealershipId = useDealershipId();
  const { priceDropRestrictedStates } = useConfiguration();

  const onError = useCallback((error: Error, context: ErrorContext) => {
    generateErrorLogger(
      `NotFoundSearchBar - ${context.location}`,
      context
    )(error);
  }, []);

  const onSuggestionSelection = useCallback(
    ({ url }: SuggestionSelectionProps) => {
      router.push(url);
    },
    [router]
  );

  useTrackRendered(
    OPEN_TEXT_SEARCH_EXPERIMENT,
    (isFocused || isModalOpen) && inputValue.length > 0
  );

  const searchBarProps = {
    apiBaseUrl: apikUrl,
    cdnBaseUrl: assetsCdnUrl,
    classes: { modalButton: 'h-[48px]' },
    dealershipId: dealershipId ?? undefined,
    disableScrollLock: true,
    geolocationState,
    inputValue,
    isMobile,
    isTablet,
    isTextSearchEnabled,
    onCloseModal: () => setIsModalOpen(false),
    onError,
    onFocus: setIsFocused,
    onShowModalClick: () => setIsModalOpen(true),
    onSuggestionSelection,
    priceDropRestrictedStates,
    refSource: 'error-page',
    setInputValue,
    showButton: isMobile,
    size: 'lg' as const,
    suppressPriceDropContent,
    trackEvent
  };

  return (
    <>
      <NotFoundSearchBarOverlay isVisible={isFocused} />
      <div
        className="relative z-30 text-left"
        data-testid="not-found-search-bar"
      >
        <MerchSearchBar {...searchBarProps} />
        {isMobile && (
          <NotFoundSearchBarModal
            {...searchBarProps}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          />
        )}
      </div>
    </>
  );
}
